import { Button } from "@mui/material";
import { useEffect, useState, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import Page from "../components/page";
import LoadingBackdrop from "../components/loading-backdrop";
import { CustomerType } from "../models/customer-type";
import { customerTypesApi } from "../services/api/customer-types";
import { customersApi } from "../services/api/customers";
import CustomerForm, { CustomerFormValues } from "../components/customer-form";
import { ErrorContext } from "../components/app-routes";
import { Customer } from "../models/customer";
import { DateTime } from "luxon";
import mixpanel from "mixpanel-browser";
import { goBackOrTo } from "../utils/history";
import StickyActionBar from "../components/sticky-action-bar";
import { getCustomLabel } from "../utils/custom-labels";
import { UserContext } from "../components/app-routes";

export default function AddCustomerPage() {
  const history = useHistory();
  const [data, setData] = useState<{
    customerTypes: CustomerType[];
    customers: Customer[];
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const { handleError } = useContext(ErrorContext);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    const load = async () => {
      setBusy(true);
      try {
        const [customerTypes, customers] = await Promise.all([
          customerTypesApi.getAll(),
          customersApi.getAll(),
        ]);
        if (isMounted.current) {
          setData({ customerTypes, customers });
        }
      } catch (error) {
        if (isMounted.current) {
          handleError(error);
        }
      } finally {
        if (isMounted.current) {
          setBusy(false);
        }
      }
    };

    load();

    return () => {
      isMounted.current = false;
    };
  }, [handleError]);

  const [wasSaved, setWasSaved] = useState(false);

  const handleSubmit = async (values: CustomerFormValues) => {
    setBusy(true);
    try {
      await customersApi.add({
        ...values,
        id: 0,
        typeId: Number(values.typeId),
        archivedAt: values.archived ? DateTime.now().toISO() : null,
      });

      mixpanel.track("Add Customer");
      setWasSaved(true);
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (wasSaved) {
      history.replace("/home/customers");
    }
  }, [wasSaved]);

  const defaultCustomerValues: CustomerFormValues = {
    name: "",
    typeId: "",
    contactFirstName: "",
    contactLastName: "",
    contactEmail: "",
    contactPhoneNumber: "",
    businessEmail: "",
    businessPhoneNumber: "",
    otherPhoneNumber: "",
    addressLine1: "",
    addressLine2: "",
    locality: "",
    postalCode: "",
    region: "",
    country: "",
    website: "",
    generalComments: "",
    deliveryInstructions: "",
    paymentInstructions: "",
    deliveryRoute: "",
    archived: false,
  };

  const context = useContext(UserContext);
  const grower = context.grower;

  return (
    <Page title={`Add ${getCustomLabel(grower ?? null, "customers")}`}>
      {data && (
        <CustomerForm
          onSubmit={handleSubmit}
          customerTypes={data.customerTypes}
          customers={data.customers}
          initialValues={defaultCustomerValues}
          saved={wasSaved}
        >
          <StickyActionBar position="bottom">
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => goBackOrTo(history, "/home/customers")}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={busy}
            >
              Add
            </Button>
          </StickyActionBar>
        </CustomerForm>
      )}
      <LoadingBackdrop open={busy} />
    </Page>
  );
}
